import { Alert, AlertType } from '@design/components/alert/Alert';
import { Button } from '@design/components/button/Button';
import { TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { useScheduleDrawerUrlSync } from '@terminal/hooks/use-schedule-drawer-url-sync/useScheduleDrawerUrlSync';
import { useListScheduledQueries } from '@terminal/react-query-hooks/alfred/useScheduledQueries';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import {
  ToastPosition,
  useToasts,
} from '@terminal/stores/use-toasts/useToasts';
import { Feedback } from '@terminal/views/alfred/quick-analysis/use-chat/assistant-response.type';
import { CopyConversation } from '@terminal/views/alfred/research/copy-conversation/CopyConversation';
import { MAX_SCHEDULED_TASKS } from '@terminal/views/alfred/research/history-sidebar/HistorySidebar';
import { PrintPdfBtn } from '@terminal/views/alfred/research/print-pdf-btn/PrintPdfBtn';
import { ConversationTask } from '@terminal/views/alfred/research/use-chat/use-chat.types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ChatFeedbackButtons.module.scss';

const TOAST_ID = 'chat-feed-back';
export const CHAT_FEEDBACK_ID = 'chat-feedback';

export type ChatFeedbackButtonsProps<F extends string> = {
  addFeedback: (feedback: F) => void;
  onBadFeedback?: () => void;
  feedback?: F;
  task?: ConversationTask;
  showingSteps?: boolean;
  conversationId?: string;
};

export const ChatFeedbackButtons = <T extends string>({
  addFeedback,
  onBadFeedback,
  feedback,
  task,
  showingSteps = false,
  conversationId,
}: ChatFeedbackButtonsProps<T>) => {
  const { t } = useTranslation('chat');
  const { showToast } = useToasts();
  const { trackAlfredFeedback } = usePosthogEvents();
  const { open: openScheduleDrawer } = useScheduleDrawerUrlSync();

  const isScheduledTaskEnabled = useFlags(state =>
    state.getFeatureFlagState('scheduled_task_feature')
  );

  const { data: scheduledQueriesData, isLoading: isScheduledQueriesLoading } =
    useListScheduledQueries({ status: 'active' }, isScheduledTaskEnabled);
  // Treat an unknown count (still loading) as at the limit to avoid a first-load
  // window where the button is enabled for users already at the cap.
  const isAtMaxScheduledTasks =
    isScheduledTaskEnabled &&
    (isScheduledQueriesLoading ||
      (scheduledQueriesData?.total_count ?? 0) >= MAX_SCHEDULED_TASKS);

  const onFeedbackButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const feedback = e.currentTarget.name as T;

    if (feedback === Feedback.Good) {
      trackAlfredFeedback(POSTHOG_TRACKING_SOURCES.ALFRED, 'thumbs_up');
    } else if (feedback === Feedback.Bad) {
      trackAlfredFeedback(POSTHOG_TRACKING_SOURCES.ALFRED, 'thumbs_down');
    }

    addFeedback(feedback);

    if (feedback === Feedback.Bad && onBadFeedback) {
      onBadFeedback();
    }

    if (feedback === Feedback.Good) {
      showToast({
        id: TOAST_ID,
        hideToast: true,
        position: ToastPosition.TopRight,
        content: (
          <Alert
            type={AlertType.Success}
            message={t('chat:feedback')}
            withIcon
          />
        ),
      });
    }
  };

  return (
    <div
      id={CHAT_FEEDBACK_ID}
      data-testid={CHAT_FEEDBACK_ID}
      className={styles.buttonsWrapper}
    >
      {task && conversationId && (
        <>
          {isScheduledTaskEnabled && (
            <TooltipContainer
              label={
                isAtMaxScheduledTasks
                  ? t('chat:historySidebar.scheduleTaskDisabledMaxReached')
                  : t('chat:historySidebar.scheduleTask')
              }
              inPortal
              trigger={[TooltipTrigger.Hover]}
            >
              <Button
                data-testid="schedule-task-button"
                variant="empty"
                size="xsmall"
                iconName="Clock"
                disabled={isAtMaxScheduledTasks}
                className={styles.scheduleButton}
                onClick={() => openScheduleDrawer(task?.input)}
              />
            </TooltipContainer>
          )}
          <CopyConversation
            task={task}
            showingSteps={showingSteps}
            questionOnly={false}
            size="xsmall"
            conversationId={conversationId}
          />
          <PrintPdfBtn
            task={task}
            showingSteps={showingSteps}
            conversationId={conversationId}
          />
        </>
      )}
      {(feedback === Feedback.Good || !feedback) && (
        <Button
          data-testid="button-positive-feedback"
          variant="empty"
          size="xsmall"
          iconName="ThumbsUp"
          disabled={feedback === Feedback.Good}
          onClick={onFeedbackButtonClick}
          name={Feedback.Good}
        />
      )}
      {(feedback === Feedback.Bad || !feedback) && (
        <Button
          data-testid="button-negative-feedback"
          variant="empty"
          size="xsmall"
          iconName="ThumbsDown"
          onClick={onFeedbackButtonClick}
          name={Feedback.Bad}
          disabled={feedback === Feedback.Bad}
        />
      )}
    </div>
  );
};
